import { useMutation } from '@apollo/client';
import {
  CancelOutlined,
  ExpandMore,
  Send,
  RestartAlt,
  NotificationsActiveOutlined,
  DeleteOutlined,
  PrintOutlined,
  NavigateNext,
} from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  PopoverOrigin,
  Stack,
  Typography,
} from '@mui/material';
import { Permission } from '@procureai/permissions';
import { Restricted, usePermissions } from '@procureai/permissions-ui';
import { useButtonMenu, useConfirmationDialog, usePrint, useCustomSnackbar } from '@procureai/ui-mui';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

import {
  TlscpEntityVariant,
  TsrctCommentParentVariant,
  TsrctNegotiationCase,
  TsrctNegotiationCaseStatus,
} from '@/generated/gql/graphql';
import { CommentsPopoverButton } from '@/legacy/components/CommentsPopoverButton/CommentsPopoverButton';
import { ModulePageHeader, ModulePageHeaderProps } from '~/components/ModulePageHeader/ModulePageHeader';
import { NegotiationCancelDialog } from '~/components/NegotiationCancelDialog/NegotiationCancelDialog';
import { NegotiationCaseIsReleasableButton } from '~/components/NegotiationCaseIsReleasableButton/NegotiationCaseIsReleasableButton';
import { NegotiationProcessStatusChip } from '~/components/NegotiationProcessStatusChip/NegotiationProcessStatusChip';
import { SupplierHeading } from '~/components/SupplierHeading/SupplierHeading';
import { useQueryPollingUntil } from '~/hooks';
import { Action, Target, useTrackInteraction } from '~/hooks/useTrackInteraction/useTrackInteraction';
import { BusinessLogPopoverButton } from '~/modules/BusinessLogWidget/components/BusinessLogPopoverButton/BusinessLogPopoverButton';
import { RouteId } from '~/routes';

import { NEGOTIATION_CASE_DELETE_MUTATION } from './NegotiationCaseDeleteMutation.gql';
import { NEGOTIATION_CASE_RELEASE_MUTATION } from './NegotiationCaseReleaseMutation.gql';
import { NEGOTIATION_CASE_REMIND_MUTATION } from './NegotiationCaseRemindMutation.gql';
import { NEGOTIATION_CASE_RESET_MUTATION } from './NegotiationCaseResetMutation.gql';
import { NEGOTIATION_DETAIL_QUERY } from './NegotiationDetail.gql';

const ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
};
const TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'center',
};

const CANCEL_DISABLED_STATES: { [key in TsrctNegotiationCaseStatus]?: true } = {
  [TsrctNegotiationCaseStatus.AWARDED]: true,
  [TsrctNegotiationCaseStatus.CANCELLED_AFTER_INVITE_EMAIL_SENT]: true,
  [TsrctNegotiationCaseStatus.CANCELLED_BEFORE_INVITE_EMAIL_SENT]: true,
  [TsrctNegotiationCaseStatus.OFFER_DISMISSED]: true,
};

const REMIND_ENABLED_STATES: { [key in TsrctNegotiationCaseStatus]?: true } = {
  [TsrctNegotiationCaseStatus.AGREEMENT_REACHED]: true,
  [TsrctNegotiationCaseStatus.CONTACT_DETAILS_CONFIRMED]: true,
  [TsrctNegotiationCaseStatus.INVITE_EMAIL_SENT]: true,
  [TsrctNegotiationCaseStatus.LINK_OPENED]: true,
  [TsrctNegotiationCaseStatus.NEGOTIATION_DETAILS_CONFIRMED]: true,
  [TsrctNegotiationCaseStatus.NEGOTIATION_OPENED]: true,
};

export interface NegotiationDetailHeaderProps {
  id: string;
  tabs?: ModulePageHeaderProps['tabs'];
}

export const NegotiationDetailHeader = ({ id, tabs }: NegotiationDetailHeaderProps) => {
  const { t } = useTranslation();
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const { confirm } = useConfirmationDialog();
  const { menuProps, buttonProps } = useButtonMenu();
  const navigate = useNavigate();
  const { print } = usePrint();
  const { hasPermission } = usePermissions();
  const variables = {
    id,
  };
  const track = useTrackInteraction();
  const [cancelOpen, setCancelOpen] = useState(false);

  const { data, loading, startPolling, polling } = useQueryPollingUntil(NEGOTIATION_DETAIL_QUERY, {
    variables,
    onError,
  });

  const refetchQueries = [{ query: NEGOTIATION_DETAIL_QUERY, variables }];

  const [release] = useMutation(NEGOTIATION_CASE_RELEASE_MUTATION, {
    onError: onError,
  });

  const [reset] = useMutation(NEGOTIATION_CASE_RESET_MUTATION, {
    refetchQueries,
    onError,
  });

  const [remind] = useMutation(NEGOTIATION_CASE_REMIND_MUTATION, {
    refetchQueries,
    onError,
  });

  const [del] = useMutation(NEGOTIATION_CASE_DELETE_MUTATION, {
    onError,
  });

  const handleDialogCancel = () => {
    setCancelOpen(false);
  };

  const { demand, isReleasable, processStatus, processSteps, sequenceNumber, status, supplier } =
    data?.tesseract.negotiationCase ?? ({} as Partial<TsrctNegotiationCase>);

  const isInitialized = status === TsrctNegotiationCaseStatus.INITIALIZED;
  const canBeReleased = isInitialized && isReleasable;

  return (
    <>
      <ModulePageHeader
        tabs={tabs}
        title={
          <Breadcrumbs aria-label="demand-navigation" separator={<NavigateNext fontSize="small" />}>
            {hasPermission(Permission.DemandList) && (
              <Link
                variant="h6"
                underline="hover"
                color="text.primary"
                component={RouterLink}
                to={RouteId.demandDetail(demand?.id)}
                data-testid="NegotiationDetail/Header/Demand"
              >
                {t('NegotiationDetail.Header.demand', { no: demand?.sequenceNumber ?? '' })}
              </Link>
            )}

            <Typography
              variant="h6"
              sx={{
                color: 'text.primary',
              }}
            >
              {t('NegotiationDetail.Header.title', { no: sequenceNumber ?? '', supplierName: supplier?.name || '' })}
            </Typography>
          </Breadcrumbs>
        }
        left={
          loading ? (
            <CircularProgress size={20} />
          ) : (
            <Box
              sx={{
                ml: 2,
              }}
            >
              <NegotiationProcessStatusChip processStatus={processStatus} status={status} />
            </Box>
          )
        }
        center={
          <Stack
            spacing={3}
            direction="row"
            sx={{
              overflow: 'hidden',
              justifyContent: 'center',
            }}
          >
            <SupplierHeading supplierId={supplier?.internalId} />
            {isInitialized && (
              <NegotiationCaseIsReleasableButton
                demandId={demand?.id}
                negotiationCaseId={id ?? ''}
                isReleasable={isReleasable}
                list={processSteps}
              />
            )}
          </Stack>
        }
        right={
          <Stack direction="row" spacing={3} sx={{ alignItems: 'center' }}>
            <CommentsPopoverButton entityId={id} entityVariant={TsrctCommentParentVariant.NEGOTIATION_CASE} />
            <BusinessLogPopoverButton entityVariant={TlscpEntityVariant.NEGOTIATION_CASE} entityId={id} />
            <Button
              loading={polling}
              variant="contained"
              color="primary"
              endIcon={<ExpandMore fontSize="small" />}
              {...buttonProps}
              data-intercom-target="NegotiationDetails/Actions"
              data-testid="NegotiationDetails/Actions"
            >
              {t('NegotiationDetail.Header.actionsButton')}
            </Button>
          </Stack>
        }
      />
      <Menu {...menuProps} anchorOrigin={ANCHOR_ORIGIN} transformOrigin={TRANSFORM_ORIGIN}>
        <Restricted needs={{ permission: Permission.CaseRelease }}>
          <MenuItem
            disabled={!canBeReleased}
            data-intercom-target="NegotiationDetails/Actions/SendToSupplier"
            data-testid="NegotiationDetails/Actions/SendToSupplier"
            onClick={() => {
              (async () => {
                startPolling((d) => {
                  return d?.tesseract.negotiationCase.status === TsrctNegotiationCaseStatus.INVITE_EMAIL_SENT;
                });
                const { errors } = await release({ variables: { id } });
                if (!errors) {
                  enqueueSuccessSnackbar(t(`NegotiationDetail.sendToSupplierSuccess`));
                  track(Target.NEGOTIATION_CASE, Action.SENT, { id });
                }
              })();
            }}
          >
            <ListItemIcon>
              <Send fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('NegotiationDetail.Header.actionSendToSupplier')} />
          </MenuItem>
        </Restricted>
        <Restricted needs={{ permission: Permission.CaseCancel }}>
          <MenuItem
            disabled={status ? CANCEL_DISABLED_STATES[status] : true}
            data-intercom-target="NegotiationDetails/Actions/Cancel"
            onClick={() => {
              setCancelOpen(true);
            }}
          >
            <ListItemIcon>
              <CancelOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('NegotiationDetail.Header.actionCancel')} />
          </MenuItem>
        </Restricted>
        <Restricted needs={{ permission: Permission.CaseReset }}>
          <MenuItem
            data-intercom-target="NegotiationDetails/Actions/Reset"
            onClick={confirm(
              async () => {
                const { errors } = await reset({ variables: { id } });
                if (!errors) {
                  enqueueSuccessSnackbar(t(`NegotiationDetail.resetSuccess`));
                  track(Target.NEGOTIATION_CASE, Action.RESET, { id });
                }
              },
              {
                message: <Trans i18nKey="NegotiationDetail.Header.actionResetConfirm" />,
              },
            )}
          >
            <ListItemIcon>
              <RestartAlt fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('NegotiationDetail.Header.actionReset')} />
          </MenuItem>
        </Restricted>

        <MenuItem
          disabled={status ? !REMIND_ENABLED_STATES[status] : true}
          data-intercom-target="NegotiationDetails/Actions/Remind"
          onClick={() => {
            (async () => {
              const { errors } = await remind({ variables: { id } });
              if (!errors) {
                enqueueSuccessSnackbar(t(`NegotiationDetail.remindSuccess`));
                track(Target.NEGOTIATION_CASE_REMINDER, Action.SENT, { id });
              }
            })();
          }}
        >
          <ListItemIcon>
            <NotificationsActiveOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('NegotiationDetail.Header.actionRemind')} />
        </MenuItem>
        <MenuItem onClick={print} data-intercom-target="NegotiationDetails/Actions/Print">
          <ListItemIcon>
            <PrintOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Export/Print" />
        </MenuItem>
        <Restricted needs={{ permission: Permission.CaseDelete }}>
          <MenuItem
            data-intercom-target="NegotiationDetails/Actions/Delete"
            onClick={confirm(
              async () => {
                const { errors } = await del({ variables: { id } });
                if (!errors) {
                  enqueueSuccessSnackbar(t(`NegotiationDetail.deleteSuccess`));
                  track(Target.NEGOTIATION_CASE, Action.DELETED, { id });
                  navigate(RouteId.negotiationCases);
                }
              },
              {
                message: <Trans i18nKey="NegotiationDetail.Header.actionDeleteConfirm" />,
              },
            )}
          >
            <ListItemIcon>
              <DeleteOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('NegotiationDetail.Header.actionDelete')} />
          </MenuItem>
        </Restricted>
      </Menu>
      <NegotiationCancelDialog
        negotiationCaseId={id}
        open={cancelOpen}
        onClose={handleDialogCancel}
        refetchQueries={refetchQueries}
      />
    </>
  );
};
